import {
  Component,
  ComponentFactory,
  ComponentFactoryResolver,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewContainerRef,
  ComponentRef,
  AfterViewInit
} from '@angular/core';
import { NavigationExtras } from '@angular/router';

import { Store } from '@ngrx/store';
import { Observable, Subscription, combineLatest, of } from 'rxjs';
import { map, filter, take, shareReplay, startWith } from 'rxjs/operators';

import { RootState } from '../../../../reducers';
import { dispatchInfoMessage, navigate, setTitle } from 'src/app/actions/core.actions';
import { getCurrentDateTime } from 'src/app/actions/current-date-time.actions';
import * as CommonIdSearchActions from 'src/app/actions/common-id/common-id-search.actions';
import * as CommonIdFindUniversitiesActions from 'src/app/actions/common-id/common-id-find-universities.actions';
import * as StaticDataSelectors from '../../../../selectors/static-data.selectors';
import {
  getMatchedUniversityCount,
  getUniversities,
  getUniversityCountSearching,
  getUniversitySearching
} from 'src/app/selectors/common-id/common-id-search.selectors';
import { getCommonIdSignedInUser } from 'src/app/selectors/common-id/common-id-auth.selectors';
import { getCurrentDateTime as getCurrentDateTimeSelector } from 'src/app/selectors/current-date-time.selectors';
import * as CommonIdUniversityBookmarkSelectors from 'src/app/selectors/common-id/common-id-university-bookmark.selectors';
import { getCommonIdVisitedPapers } from 'src/app/selectors/common-id/common-id-visited-paper.selectors';

import { RoutingPathResolver } from '../../../../app-routing-path-resolver';
import {
  COMMON_ID_FREE_YEARS,
  COMMON_ID_PREFECTURES,
  COMMON_ID_SEARCH_YEARS,
  COMMON_ID_YEAR_LABEL_FOR_UNPAID_USERS,
  COMMON_TEST_PREFECTURE_NAME,
  COMMON_TEST_UNIVERSITY_ID,
  DEFAULT_DEPARTMENT_CATEGORY
} from '../../../../resources/common-id-config';

import { UniversitySearchCondition, UniversitySearchType, UniversityCondition, ExamType } from 'src/app/models/university-search-condition';
import { DepartmentCategory, University } from 'src/app/models/common-data';
import { StaticCommonData } from '../../../../models/static-common-data';
import { CommonIdUser } from 'src/app/models/common-id/common-id-user';
import { CurrentDateTime } from 'src/app/models/current-date-time';
import { CommonIdFindUniversityPapersResponse } from 'src/app/models/common-id/common-id-find-university-papers-response';
import { FindUniversityPapersResult } from 'src/app/models/find-university-papers-result';
import { UniversityBookmark } from 'src/app/models/common-id/common-id-bookmark';

import { UniversitySearchRouterService } from 'src/app/services/university-search-router.service';
import { UniversitySearchResultSharedService } from 'src/app/services/university-search-result-shared.service';
import { Log } from 'src/app/utils/log';
import { Diff } from 'src/app/utils/diff';
import { UniversitySearchQueryParamsMapper } from 'src/app/mappers/university-search-query-params-mapper';
import { SubjectUtil } from 'src/app/utils/subject-util';
import { CommonIdSubjectUtil } from 'src/app/utils/common-id/common-id-subject-util';

import { CommonIdSearchUnivResultComponent } from '../search-univ-result/search-univ-result.component';
import { CommonIdUserUtil } from 'src/app/utils/common-id/common-id-user-util';
import { DataUtil } from '../../../../utils/data-util';
import { setCommonIdBrowserTitle } from '../../../../actions/common-id/common-id-core.actions';
import { ChangeValuesParams, Year } from '../../../../models/search-univ-interfaces';
import { CommonIdFindUniversitiesRequest } from 'src/app/models/common-id/common-id-find-universities-request';
import { getCommonIdFindUniversitiesResult } from 'src/app/selectors/common-id/common-id-find-universities.selectors';

interface DesiredUniversity extends University {
  isdisabled?: boolean;
}

@Component({
  selector: 'app-common-id-search-univ',
  templateUrl: './search-univ.component.html',
  styleUrls: ['./search-univ.component.scss']
})
export class CommonIdSearchUnivComponent implements OnInit, OnDestroy, AfterViewInit {
  factory: ComponentFactory<CommonIdSearchUnivResultComponent>;
  @ViewChild('searchUnivResult', { static: true, read: ViewContainerRef }) viewContainerRef: ViewContainerRef;
  searchResultComponent: ComponentRef<CommonIdSearchUnivResultComponent>;
  previousPageSearchResultComponents: ComponentRef<CommonIdSearchUnivResultComponent>[] = [];

  constructor(
    private resolver: ComponentFactoryResolver,
    private store: Store<RootState>,
    private universitySearchRouterService: UniversitySearchRouterService,
    private universitySearchResultSharedService: UniversitySearchResultSharedService
  ) {}

  private LOG_SOURCE = this.constructor.name;
  private title = '大学名で探す';
  private lastNotifiedCondition: UniversitySearchCondition<UniversityCondition> | undefined;
  private universityCountSubscription: Subscription;
  private bookmarkClickEmittedSubsription: Subscription;
  private errorUniversityIdSubsription: Subscription;
  private findUniversitiesSubscription: Subscription;

  defaultDepartmentCategory: DepartmentCategory = DEFAULT_DEPARTMENT_CATEGORY;

  isSearchSummaryDisplayed: boolean;

  isPremiumUser: boolean;
  matchedUniversityCount: number | undefined;

  selectedUniversities: University[] = [];
  selectableYears: Year[];
  selectedYear: string | null;
  selectedDepartmentCategory: DepartmentCategory;
  selectedExamType: ExamType = ExamType.ALL;

  matchedUniversityCount$: Observable<number>;
  universityCountSearching$: Observable<boolean>;
  searchButtonDisabled$: Observable<boolean>;

  staticCommonData$: Observable<StaticCommonData>;
  staticCommonData: StaticCommonData;
  signedInUser$: Observable<CommonIdUser>;

  pageToSearch: number;
  currentPage: number;
  universities$: Observable<CommonIdFindUniversityPapersResponse[]>;
  searchResults: FindUniversityPapersResult[] = [];
  searchResultsToDisplay: FindUniversityPapersResult[] = [];

  isUniversitySearching$: Observable<boolean>;
  isShowMoreButtonShown$: Observable<boolean>;
  pageButton: number;

  isPremiumIconShown: boolean;
  currentDateTime$: Observable<CurrentDateTime>;

  universityBookmark: UniversityBookmark;
  universityBookmark$: Observable<UniversityBookmark>;

  hasDesiredUniversities = false;
  desiredUniversities$: Observable<DesiredUniversity[]> = of([]);

  commonIdVisitedPapers$: Observable<string[]>;
  commonIdVisitedPapers: string[];

  previousCondition: UniversitySearchCondition<UniversityCondition>;

  ngOnInit() {
    this.store.dispatch(setCommonIdBrowserTitle({ subTitle: this.title }));
    setTimeout(() => this.store.dispatch(setTitle({ title: this.title })));

    // 動的に大学検索結果コンポーネントをコンパイルできる状態にする
    this.factory = this.resolver.resolveComponentFactory(CommonIdSearchUnivResultComponent);

    this.setUpCurrentDateTime();
    this.setUpUser();
    this.setUpYears();
    this.setUpStaticCommonData();
    this.setUpCommonIdVisitedPapers();
    this.setUpUniversityBookmarks();
    this.setUpMatchedUniversityCount();
    this.initializeSearchResults();
    this.setUpIsUniversitySearching();
    this.setUpDesiredUniversities();

    // 大学詳細画面からの戻りであれば元の検索条件をセットし検索する
    this.previousCondition = this.universitySearchRouterService.getPreviousSearchCondition();
    if (this.universitySearchRouterService.isFromLocationBack && this.previousCondition) {
      this.findUniversityByPreviousCondition(this.previousCondition);
    }

    // お気に入り登録ボタンに関する処理
    this.passBookmarkDataToChildComponents();
  }

  ngAfterViewInit() {
    setTimeout(() => window.scrollTo(0, 0));
  }

  ngOnDestroy() {
    if (this.universityCountSubscription) this.universityCountSubscription.unsubscribe();
    this.universityCountSubscription = undefined;
    if (this.bookmarkClickEmittedSubsription) this.bookmarkClickEmittedSubsription.unsubscribe();
    if (this.errorUniversityIdSubsription) this.errorUniversityIdSubsription.unsubscribe();
    if (this.findUniversitiesSubscription) this.findUniversitiesSubscription.unsubscribe();

    this.store.dispatch(CommonIdSearchActions.initializeUniversityCountState());
    this.store.dispatch(CommonIdSearchActions.initializeUniversityPapersState());

    if (this.searchResultComponent) {
      // 都道府県で探すタブを選択した際、searchResultComponentはundefinedになる
      this.searchResultComponent.destroy();
    }
    if (this.previousPageSearchResultComponents.length > 0) {
      this.previousPageSearchResultComponents = [];
    }
  }

  menuClickHandler(key) {
    switch (key) {
      case 'school':
        this.store.dispatch(navigate({ url: RoutingPathResolver.resolveCommonIdSearchUniv() }));
        break;
      case 'edit_note':
        this.store.dispatch(navigate({ url: RoutingPathResolver.resolveCommonIdSearchUnivPref() }));
        break;
    }
  }

  resetForms(event: ChangeValuesParams) {
    // 共通コンポーネントより、最新の検索条件を取得
    this.getCurrentSearchConditionFromBaseComponent(event);

    this.lastNotifiedCondition = undefined;

    this.store.dispatch(CommonIdSearchActions.initializeUniversityCountState());
    this.store.dispatch(CommonIdSearchActions.initializeUniversityPapersState());

    this.store.dispatch(navigate({ url: RoutingPathResolver.resolveCommonIdSearchUniv(), extras: {} }));
    this.initializeSearchResults();

    if (this.viewContainerRef) this.viewContainerRef.clear();
    if (this.searchResultComponent) this.searchResultComponent.destroy();
    if (this.previousPageSearchResultComponents.length > 0) {
      this.previousPageSearchResultComponents = [];
    }
  }

  showSearchResultView() {
    const condition = this.getCurrentSearchCondition();
    this.setFirstPageSearchResults(condition);

    const extras: NavigationExtras = {
      queryParams: UniversitySearchQueryParamsMapper.encodeUniversitySearchConditionWithUniversityIds(condition)
    };
    this.store.dispatch(navigate({ url: RoutingPathResolver.resolveCommonIdSearchUniv(), extras }));

    // 結果を表示ボタンを無効にする
    this.searchButtonDisabled$ = of(true);
  }

  onChangeValues(event: ChangeValuesParams) {
    // 共通コンポーネントより、最新の検索条件を取得
    this.getCurrentSearchConditionFromBaseComponent(event);

    // 年度の値を取得 + 王冠アイコンの表示有無フラグを設定
    this.setUpIsPremiumIconShown();

    // 現在の選択条件を取得して保存
    const currentCondition = this.getCurrentSearchCondition();
    Log.debug(this.LOG_SOURCE, '現在の選択状況: ', currentCondition);

    if (!Diff.isDifferentObject(this.lastNotifiedCondition, currentCondition)) {
      Log.debug(this.LOG_SOURCE, '前回検索した条件と同じため無視します');
      return;
    }

    this.lastNotifiedCondition = { ...currentCondition };
    Log.debug(this.LOG_SOURCE, '検索項目が変更されました');

    // 検索条件が変わる度に、検索結果欄を非表示にする
    this.initializeSearchResults();
    if (this.viewContainerRef) {
      this.viewContainerRef.clear();
    }
    this.setUpSearchResultComponent();
    this.isShowMoreButtonShown$ = of(false);

    // 検索条件が未入力の場合は、大学校数検索処理をせず、結果も表示しない
    if (this.selectedUniversities.length === 0 || this.selectedYear === undefined) {
      Log.warn(this.LOG_SOURCE, '検索条件が未入力です');
      this.store.dispatch(CommonIdSearchActions.initializeUniversityCountState());
    } else {
      this.findUniversityCount(currentCondition);
    }

    // 検索条件が未入力の場合、または、検索結果が０件の場合は検索ボタンをクリック不可にする
    this.setSearchButtonDisabled();
  }

  onDesiredUnivClick(universityId: string) {
    this.store.dispatch(navigate({ url: RoutingPathResolver.resolveCommonIdSearchUnivDetail(universityId) }));
  }

  showMoreResults() {
    this.isShowMoreButtonShown$ = of(true);
    const condition = this.getCurrentSearchCondition();
    this.pageToSearch = this.currentPage + 1;

    // 検索結果用の配列を初期化する
    this.initializeSearchResults();

    this.findUniversities(condition, this.pageToSearch);

    // 前ページの検索結果コンポーネントをストアする - 前ページのお気に入りボタンも無効にするため
    const prevIndex = this.currentPage - 2;
    this.previousPageSearchResultComponents[prevIndex] = this.searchResultComponent;

    // 検索結果コンポーネントを表示する
    this.isSearchSummaryDisplayed = true;
    this.setUpSearchResultComponent();
  }

  setFirstPageSearchResults(condition: UniversitySearchCondition<UniversityCondition>) {
    // 検索結果用の配列を初期化する
    this.initializeSearchResults();

    // 大学検索用APIから検索結果を取得する
    this.pageToSearch = 1;
    this.findUniversities(condition, this.pageToSearch);

    // 検索結果コンポーネントを表示する
    this.isSearchSummaryDisplayed = true;

    this.viewContainerRef.clear();
    this.setUpSearchResultComponent();
  }

  private findUniversityCount(condition: UniversitySearchCondition<UniversityCondition>) {
    setTimeout(() => {
      this.store.dispatch(CommonIdSearchActions.findUniversityCountByUniversityIds({ condition, logging: true }));
    });
  }

  private findUniversities(condition: UniversitySearchCondition<UniversityCondition>, page: number) {
    Log.debug(this.LOG_SOURCE, `選択された page: ${page}`);
    this.currentPage = page;

    this.store.dispatch(CommonIdSearchActions.initializeUniversityPapersState());
    this.store.dispatch(CommonIdSearchActions.findUniversityPapersByUniversityIds({ condition, page }));

    this.universities$ = this.store.select(getUniversities).pipe(filter(it => it != null));
    if (this.findUniversitiesSubscription) this.findUniversitiesSubscription.unsubscribe();
    this.findUniversitiesSubscription = combineLatest([
      this.universities$,
      this.staticCommonData$,
      this.commonIdVisitedPapers$.pipe(startWith<string[]>([])),
      this.universityBookmark$.pipe(startWith(null))
    ])
      .pipe(take(1))
      .subscribe(([universities, staticCommonData, commonIdVisitedPapers, universityBookmark]) => {
        this.staticCommonData = staticCommonData;
        this.commonIdVisitedPapers = commonIdVisitedPapers;
        this.universityBookmark = universityBookmark;

        // 大学検索結果に、教科名称、閲覧済みフラグ、アイコンの表示有無フラグを追加
        this.searchResults = universities.map(university => {
          const isCommonTest: boolean = university.universityId === COMMON_TEST_UNIVERSITY_ID ? true : false;
          const departmentResults = university.departments.map(department => {
            return {
              ...department,
              papers: department.papers.map(paper => {
                return {
                  ...paper,
                  paperIds: paper.paperIds.map(paperId => {
                    const subjectName = CommonIdSubjectUtil.getSubjectName(paperId.paperId, this.staticCommonData.subjects);

                    return {
                      id: paperId.paperId,
                      daimonIds: paperId.daimonIds,
                      subjectName,
                      isVisited:
                        this.commonIdVisitedPapers &&
                        this.commonIdVisitedPapers.find(commonIdVisitedPaper => commonIdVisitedPaper === paperId.paperId)
                          ? true
                          : false,
                      isPremiumIconShown: this.isPremiumIconShown
                    };
                  })
                };
              })
            };
          });

          const prefecture = COMMON_ID_PREFECTURES.find(pref => pref.id === university.prefectureId);
          const prefectureName = prefecture ? prefecture.name : COMMON_TEST_PREFECTURE_NAME;
          // 大学検索結果に、大学種別名と都道府県名、お気に入り登録の有無を追加
          return {
            ...university,
            universityTypeName:
              this.staticCommonData.universityTypes.find(universityType => universityType.id === university.universityType)?.name ?? '',
            prefectureName,
            departments: departmentResults,
            isBookmarked:
              this.universityBookmark &&
              this.universityBookmark.universities &&
              this.universityBookmark.universities.find(bookmarkUniversity => bookmarkUniversity.unvId === university.universityId)
                ? true
                : false
          };
        });

        // 検索結果表示用の配列に、新しいページの大学検索結果を追加 (mutateせず再代入)
        this.searchResultsToDisplay = DataUtil.sortObjectArrayBySpecificKey(
          [...this.searchResultsToDisplay, ...this.searchResults],
          'universityId'
        );
        // 動的生成された検索結果コンポーネントに新しい配列参照を伝搬する (setter 経由で内部ウィジェットの ngOnChanges 発火)
        if (this.searchResultComponent) {
          this.searchResultComponent.instance.searchResults = this.searchResultsToDisplay;
        }

        // 検索結果が１ページに４件以上存在する場合は、もっと見るボタンを表示する
        this.setUpIsShowMoreButton();
      });
  }

  private getCurrentSearchCondition(): UniversitySearchCondition<UniversityCondition> {
    const universityIds = this.selectedUniversities.map(university => university.id);

    const condition: UniversitySearchCondition<UniversityCondition> = {
      type: UniversitySearchType.UNIV,
      universityCondition: { universityIds },
      year: Number(this.selectedYear),
      examType: this.selectedExamType
    };

    if (this.selectedDepartmentCategory && this.selectedDepartmentCategory.id !== this.defaultDepartmentCategory.id) {
      condition.departmentCategoryId = Number(this.selectedDepartmentCategory.id);
    }

    return condition;
  }

  private setUpUser() {
    this.signedInUser$ = this.store.select(getCommonIdSignedInUser).pipe(
      filter<CommonIdUser>(it => it != null),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    combineLatest([this.currentDateTime$, this.signedInUser$])
      .pipe(take(1))
      .subscribe(([currentDateTime, signedInUser]) => {
        this.isPremiumUser = CommonIdUserUtil.getIsPremiumUser(currentDateTime, signedInUser);
      });
  }

  private setUpIsPremiumIconShown() {
    // 年度の値を取得 + 王冠アイコンの表示有無フラグを設定
    this.isPremiumIconShown = this.isPremiumUser || COMMON_ID_FREE_YEARS.includes(this.selectedYear) ? false : true;
  }

  private setUpCurrentDateTime() {
    this.store.dispatch(getCurrentDateTime());
    this.currentDateTime$ = this.store.select(getCurrentDateTimeSelector).pipe(
      filter(it => it != null),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private setUpStaticCommonData() {
    this.staticCommonData$ = this.store.select(StaticDataSelectors.getStaticCommonData).pipe(
      filter(it => it != null),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private setUpYears() {
    this.selectableYears = COMMON_ID_SEARCH_YEARS.map(year => {
      const label = this.isPremiumUser || COMMON_ID_FREE_YEARS.includes(year) ? year : year + COMMON_ID_YEAR_LABEL_FOR_UNPAID_USERS;
      return {
        label,
        value: year
      };
    });
  }

  private setUpCommonIdVisitedPapers() {
    this.commonIdVisitedPapers$ = this.store.select(getCommonIdVisitedPapers).pipe(
      filter(it => it != null),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private setUpMatchedUniversityCount() {
    this.matchedUniversityCount$ = this.store.select(getMatchedUniversityCount).pipe(shareReplay({ bufferSize: 1, refCount: true }));
    this.universityCountSubscription = this.matchedUniversityCount$.subscribe(matchedUniversityCount => {
      this.matchedUniversityCount = matchedUniversityCount;
    });

    this.setSearchButtonDisabled();

    this.universityCountSearching$ = this.store.select(getUniversityCountSearching);
  }

  private setSearchButtonDisabled() {
    this.searchButtonDisabled$ = combineLatest([this.matchedUniversityCount$]).pipe(
      map(
        ([matchedUniversityCount]) =>
          matchedUniversityCount == null ||
          matchedUniversityCount === 0 ||
          this.selectedUniversities.length === 0 ||
          this.selectedYear === undefined
      )
    );
  }

  private initializeSearchResults() {
    this.searchResults = [];
    this.searchResultsToDisplay = [];
    this.isSearchSummaryDisplayed = false;
  }

  private setUpIsUniversitySearching() {
    this.isUniversitySearching$ = this.store.select(getUniversitySearching);
  }

  private setUpDesiredUniversities() {
    combineLatest([this.signedInUser$])
      .pipe(take(1))
      .subscribe(([signedInUser]) => {
        if (signedInUser.desiredUniversities && signedInUser.desiredUniversities.length > 0) {
          this.hasDesiredUniversities = true;
          this.desiredUniversities$ = this.getDesiredUniversities(signedInUser.desiredUniversities);
        }
      });
  }

  private getDesiredUniversities(desiredUniversities: string[]): Observable<University[]> {
    this.store.dispatch(CommonIdFindUniversitiesActions.initializeCommonIdFindUniversitiesResult());

    const universityIds = [...new Set(desiredUniversities.map(univ => univ.split('-')[0]))];
    const request: CommonIdFindUniversitiesRequest = { universityIds };
    this.store.dispatch(CommonIdFindUniversitiesActions.commonIdFindUniversities({ request }));

    return this.store.select(getCommonIdFindUniversitiesResult).pipe(
      filter(it => it != null),
      take(1),
      map(result => {
        if (!result.success) {
          this.handleUniversityLoadError('志望校の大学情報の取得(CommonIdFindUniversities)', result.error);
          return [];
        }
        return result.response.map(univ => ({
          id: univ.universityId,
          name: univ.universityName,
          isDisabled: univ.isSearchable === false || univ.hasExternalData === false ? true : false
        }));
      })
    );
  }

  private setUpIsShowMoreButton() {
    const numOfUniversitiesShown = this.currentPage * 3;
    this.isShowMoreButtonShown$ = combineLatest([this.matchedUniversityCount$]).pipe(
      map(
        ([matchedUniversityCount]) =>
          matchedUniversityCount > numOfUniversitiesShown && this.searchResultsToDisplay && this.searchResultsToDisplay.length > 0
      )
    );
    // もっと見るボタンを表示する度に、ローディングアイコンを非表示にする
    this.pageButton = this.pageToSearch;
  }

  private setUpUniversityBookmarks() {
    this.universityBookmark$ = this.store.select(CommonIdUniversityBookmarkSelectors.getUniversityBookmark).pipe(
      filter(it => it != null),
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private setUpSearchResultComponent() {
    // 検索結果コンポーネントの生成を実行
    this.searchResultComponent = this.viewContainerRef.createComponent(this.factory);

    // データバインディング
    this.searchResultComponent.instance.searchResults = this.searchResultsToDisplay;
    this.searchResultComponent.instance.signedInUser$ = this.signedInUser$;
    this.searchResultComponent.instance.universityBookmark$ = this.universityBookmark$;
    this.searchResultComponent.instance.isPremiumIconShown = this.isPremiumIconShown;
    this.searchResultComponent.instance.isBtoC = true;
    this.searchResultComponent.instance.selectedExamType = this.selectedExamType;
  }

  private getCurrentSearchConditionFromBaseComponent(event: ChangeValuesParams) {
    this.selectedUniversities = event.universities;
    this.selectedYear = event.year;
    this.selectedDepartmentCategory = event.department;
    this.selectedExamType = event.examType;
  }

  private findUniversityByPreviousCondition(previousCondition: UniversitySearchCondition<UniversityCondition>) {
    Log.debug(this.LOG_SOURCE, `前回の検索条件: `, previousCondition);

    combineLatest([this.staticCommonData$])
      .pipe(take(1))
      .subscribe(([staticCommonData]) => {
        // 前回の大学名を設定
        previousCondition.universityCondition.universityIds.map(universityId => {
          const name = staticCommonData.universities.find(staticUniversity => staticUniversity.id === universityId).name;
          this.selectedUniversities.push({ id: universityId, name });
        });

        // 前回の年度を設定
        this.selectedYear = this.previousCondition.year.toString();

        // 前回の入試方式を設定
        this.selectedExamType = this.previousCondition.examType;

        // プレミアムアイコンの設定
        this.setUpIsPremiumIconShown();

        // 検索結果欄を非表示にする
        this.initializeSearchResults();
        if (this.viewContainerRef) {
          this.viewContainerRef.clear();
        }
        if (this.previousPageSearchResultComponents.length > 0) {
          this.previousPageSearchResultComponents = [];
        }
        this.setUpSearchResultComponent();
        this.isShowMoreButtonShown$ = of(false);

        // 前回の検索条件が未入力の場合は、大学校数検索処理をせず、結果も表示しない
        if (this.selectedUniversities.length === 0 || this.selectedYear === undefined) {
          Log.warn(this.LOG_SOURCE, '前回の検索条件が未入力です');
          this.store.dispatch(CommonIdSearchActions.initializeUniversityCountState());
        } else {
          this.findUniversityCount(this.previousCondition);
        }

        // 結果表示ボタンを無効にする
        this.searchButtonDisabled$ = of(true);
        this.setFirstPageSearchResults(previousCondition);
      });
  }

  private passBookmarkDataToChildComponents() {
    // お気に入りの登録または解除がされた場合に、前ページのお気に入りボタンも無効にする
    this.bookmarkClickEmittedSubsription = this.universitySearchResultSharedService.bookmarkClickEmitted$
      .pipe(filter(bookmarkClickEmitted => bookmarkClickEmitted !== null))
      .subscribe(bookmarkClickEmitted => {
        this.passBookmarkClickEmittedToChildComponents(bookmarkClickEmitted);
      });

    // お気に入りの登録のエラー発生時のボタンの挙動の制御
    this.errorUniversityIdSubsription = this.universitySearchResultSharedService.errorUniversityId$.subscribe(errorUniversityId => {
      this.passErrorUniversityIdToChildComponents(errorUniversityId);
    });
  }

  private passBookmarkClickEmittedToChildComponents(bookmarkClickEmitted: boolean) {
    // 全ページの検索結果コンポーネントの子コンポーネントに、お気に入り登録または解除のイベントフラグを渡す
    if (this.previousPageSearchResultComponents.length > 0) {
      this.previousPageSearchResultComponents.forEach(prevPageComponent => {
        if (prevPageComponent) {
          prevPageComponent.instance.bookmarkClickEmitted = bookmarkClickEmitted;
        }
      });
    }

    if (this.searchResultComponent) {
      this.searchResultComponent.instance.bookmarkClickEmitted = bookmarkClickEmitted;
    }
  }

  private passErrorUniversityIdToChildComponents(universityId: string) {
    // 全ページの検索結果コンポーネントの子コンポーネントに、お気に入り登録または解除のイベントフラグを渡す
    if (this.previousPageSearchResultComponents.length > 0) {
      this.previousPageSearchResultComponents.forEach(prevPageComponent => {
        if (prevPageComponent) {
          prevPageComponent.instance.errorUnivId$ = of(universityId);
        }
      });
    }

    if (this.searchResultComponent) {
      this.searchResultComponent.instance.errorUnivId$ = of(universityId);
    }
  }

  private handleUniversityLoadError(source: string, error: any) {
    Log.warn(this.LOG_SOURCE, `${source} error: err.code: ${error ? error.code : 'none'}`, error);
    this.store.dispatch(
      dispatchInfoMessage({
        message: error ? `[${error.code}] ${error.message}` : '志望校の取得中にエラーが発生しました'
      })
    );
  }
}
